import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  Button,
  Card,
  Flex,
  Form,
  Grid,
  Input,
  message,
  Pagination,
  Select,
  Table,
  Typography,
  Space,
  Badge,
} from "antd";
import { Icon } from "@iconify/react/dist/iconify.js";
import { format } from "date-fns";
import PropTypes from "prop-types";
import useTitleStore from "../../store/titleStore";
import useDebounce from "../../hooks/useDebounce";
import { useTableHeight } from "../../hooks/useTableHeight";
import UpdatedByCell from "../../components/UpdatedByCell";
import TrainingTypeTag from "../../components/TrainingTypeTag";
import StatusTag from "../../components/StatusTag";
import { COST_SOURCE_LABELS } from "../../utils/trainingCatalog.constants";
import trainingCatalogService from "../../services/training-catalog.service";
import { fetchProvinces } from "../../services/province.service";
import pallete from "../../utils/pallete";

/**
 * Reusable Training Catalog Index Component
 * Can be used for OnGoing, Accepted, and Rejected pages
 *
 * @param {Object} props
 * @param {string} props.type - Type of catalog: 'pending', 'accepted', 'rejected'
 * @param {string} props.title - Page title
 * @param {string} props.detailRoute - Route for detail page
 */
export default function TrainingCatalogIndex({
  type = "pending",
  title = "Katalog Pelatihan",
  detailRoute = "/training-catalog/pending/detail",
}) {
  const breakpoint = Grid.useBreakpoint();
  const navigate = useNavigate();
  const { setTitle } = useTitleStore();
  const tableHeight = useTableHeight();

  const [filterForm] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [keyword, setKeyword] = useState("");
  const [exportLoading, setExportLoading] = useState(false);
  const debouncedKeyword = useDebounce(keyword, 500);

  const [dataParams, setDataParams] = useState({
    limit: 10,
    page: 1,
    query: undefined,
    province: undefined,
  });

  const province = Form.useWatch("province", filterForm);

  const { data: provinces = [] } = useQuery({
    queryKey: ["provinces"],
    queryFn: fetchProvinces,
    staleTime: 24 * 60 * 60 * 1000,
  });

  const provinceOptions = useMemo(
    () =>
      provinces.map((prov) => ({
        label: prov.nama,
        value: prov.nama,
      })),
    [provinces],
  );

  const {
    data: trainingCatalog,
    isLoading,
    isRefetching,
  } = useQuery({
    queryKey: ["training-catalog", type, dataParams],
    queryFn: () => trainingCatalogService.fetchAll(type, dataParams),
    placeholderData: keepPreviousData,
  });

  // Set page title
  useEffect(() => {
    setTitle(title);
  }, [setTitle, title]);

  useEffect(() => {
    setDataParams((prev) => ({
      ...prev,
      page: 1,
      query: debouncedKeyword || undefined,
      province: province || undefined,
    }));
  }, [debouncedKeyword, province, type]);

  const handleChangePage = (page, pageSize) => {
    setDataParams((prev) => ({
      ...prev,
      limit: pageSize,
      page,
    }));
  };

  const handleExportExcel = async () => {
    try {
      setExportLoading(true);
      const response = await trainingCatalogService.exportExcel(type, {
        query: dataParams.query,
        province: dataParams.province,
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const fileName =
        response.headers["content-disposition"]
          ?.split("filename=")?.[1]
          ?.replace(/"/g, "") || `katalog-pelatihan-${type}.xlsx`;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      messageApi.success("File berhasil diunduh");
    } catch {
      messageApi.error("Gagal mengunduh file Excel");
    } finally {
      setExportLoading(false);
    }
  };

  // Memoized columns configuration
  const columns = useMemo(() => {
    const baseColumns = [
      {
        title: "No.",
        align: "center",
        width: 60,
        fixed: breakpoint.md && "left",
        render: (_, __, index) => {
          const page = trainingCatalog?.data?.pagination?.page ?? 1;
          const limit = dataParams?.limit ?? 10;

          return (page - 1) * limit + index + 1;
        },
      },
      {
        title: "Nama Lembaga",
        dataIndex: "institution",
        width: 220,
        fixed: breakpoint.md && "left",
        render: (_, record) => (
          <Space direction="vertical" size={2}>
            <Typography.Text style={{ marginBottom: 0, fontWeight: 600 }}>
              {record.institution || record.insitution || "-"}
            </Typography.Text>
            {record.province && (
              <Typography.Text
                type="secondary"
                style={{ fontSize: "13px", fontStyle: "italic" }}
              >
                {record.province}
              </Typography.Text>
            )}
          </Space>
        ),
      },
      {
        title: "Judul & Jadwal Pelatihan",
        dataIndex: "title",
        width: 250,
        // render: (val) => val ?? "-",
        render: (_, record) => (
          <>
            <Space direction="vertical" size={2}>
              <Typography.Text style={{ marginBottom: 0, fontWeight: 600 }}>
                {record.title ?? "-"}
              </Typography.Text>
              <Typography.Text
                type="secondary"
                style={{ fontSize: "12px", fontStyle: "italic" }}
              >
                {record.startDate} s/d {record.endDate}
              </Typography.Text>
            </Space>
          </>
        ),
      },
      {
        title: "Jenis Pelatihan",
        dataIndex: "category",
        width: 140,
        render: (type) => <TrainingTypeTag type={type} />,
      },
      {
        title: "Sumber Biaya",
        dataIndex: "costSources",
        width: 200,
        render: (val) => COST_SOURCE_LABELS[val],
      },
      {
        title: "Tanggal Pengajuan",
        dataIndex: "submissionDate",
        width: 150,
        render: (val) => (
          <Typography.Text
            style={{ fontSize: "13px", fontStyle: "italic" }}
          >
            {val ? format(new Date(val), "dd/MM/yyyy") : "-"}
          </Typography.Text>
        ),
      },
      {
        title: "Status",
        dataIndex: "status",
        width: 160,
        render: (status) => <StatusTag status={status} />,
      },
      {
        title: "Update By",
        width: 180,
        render: (val) => (val?.updatedBy ? <UpdatedByCell value={val} /> : "-"),
      },
      {
        className: "last-cell-p",
        title: "Aksi",
        align: "right",
        fixed: "right",
        width: 80,
        render: (record) => (
          <Button
            icon={<Icon icon="solar:eye-bold-duotone" width="18" height="18" />}
            color="default"
            variant="filled"
            onClick={() => navigate(`${detailRoute}/${record.uuid}`)}
          />
        ),
      },
    ];
    return baseColumns;
  }, [
    breakpoint.md,
    dataParams.limit,
    navigate,
    trainingCatalog?.data?.currentPage,
    detailRoute,
  ]);

  return (
    <>
      {contextHolder}
      <Card
        title={
          <Flex align="center" gap={8}>
            <Typography.Title level={5} style={{ margin: 0 }}>
              {title}
            </Typography.Title>
            <Badge
              count={trainingCatalog?.data?.pagination?.total || 0}
              showZero
              overflowCount={9999}
              style={{
                backgroundColor: pallete.primary?.[500] || "#DB8B25",
                color: "#fff",
              }}
            />
          </Flex>
        }
        styles={{
          body: {
            paddingLeft: 0,
            paddingRight: 0,
          },
        }}
      >
        <Form form={filterForm}>
          <Flex
            justify="space-between"
            align="center"
            gap={12}
            wrap="wrap"
            style={{ paddingInline: 24, marginBottom: 24 }}
          >
            <Input
              style={{ width: breakpoint.md ? 300 : "100%", minWidth: 200 }}
              prefix={
                <Icon icon="hugeicons:search-01" width="16" height="16" />
              }
              placeholder="Cari nama pelatihan..."
              onChange={(e) => setKeyword(e.target.value)}
              value={keyword}
              allowClear
            />

            <Flex gap={12} wrap="wrap">
              {/* <Form.Item noStyle name="trainingCategory">
                <Select
                  allowClear
                  placeholder="Jenis Pelatihan"
                  style={{ width: breakpoint.md ? 160 : 140 }}
                  options={TRAINING_TYPE_OPTIONS}
                />
              </Form.Item> */}

              <Form.Item noStyle name="province">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="Provinsi"
                  style={{ width: breakpoint.md ? 230 : 200 }}
                  options={provinceOptions}
                />
              </Form.Item>
              <Button
                type="primary"
                icon={
                  <Icon icon="solar:export-bold-duotone" width={18} height={18} />
                }
                loading={exportLoading}
                onClick={handleExportExcel}
              >
                Export Excel
              </Button>
            </Flex>
          </Flex>
        </Form>

        <Table
          columns={columns}
          size="small"
          loading={isLoading || isRefetching}
          rowKey="uuid"
          scroll={{ y: tableHeight, x: 1100 }}
          dataSource={trainingCatalog?.data?.data || []}
          pagination={false}
        />

        <Pagination
          style={{ marginInline: 24, marginTop: 16 }}
          total={trainingCatalog?.data?.pagination.total}
          showSizeChanger={{
            showSearch: false,
          }}
          pageSizeOptions={["10", "20", "50"]}
          current={trainingCatalog?.data?.pagination.page}
          pageSize={dataParams.limit}
          align="end"
          onChange={handleChangePage}
        />
      </Card>
    </>
  );
}

TrainingCatalogIndex.propTypes = {
  type: PropTypes.oneOf(["pending", "accepted", "rejected"]).isRequired,
  title: PropTypes.string,
  detailRoute: PropTypes.string,
};
